import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_WEEK,
  LINE_ITEM_MONTH,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { createSlug, parse, safeExternalBookingUrl, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { track } from '../../util/analytics';
import { adClickIds } from '../../util/attribution';
import { isFixdeskListing } from '../../util/fixdesk';
import { getEarliestBookableDate, resolveBookingLeadDays } from '../../util/bookingLeadTime';
import { requiresSoftReservationFallback } from '../../util/softReservation';
import { checkoutOffersWallets, walletsRegisteredForHost } from '../../util/stripePaymentRequest';
import {
  OFFER,
  REQUEST,
  getSupportedProcessesInfo,
  isBookingProcess,
  isNegotiationProcess,
  isInquiryProcess,
  isPurchaseProcess,
  isSubscriptionProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { ModalInMobile, PrimaryButton, AvatarSmall, ExternalLink, H1, H2 } from '../../components';
import { Wifi, Coffee, Armchair, PlaneTakeoff, Zap, ShieldCheck } from 'lucide-react';
// URL import (not the ReactComponent) so the logo renders as an <img> — keeps its
// explicit two-tone fills, isolated from the global `svg { fill: currentColor }` rule.
import loungePairLogo from '../../assets/loungepair-logo.svg';
import PriceVariantPicker from './PriceVariantPicker/PriceVariantPicker';
import SubmitFinePrint from './SubmitFinePrint/SubmitFinePrint';
import { hasDeliveryMethod } from './ProductOrderForm/deliveryMethod';

import css from './OrderPanel.module.css';

// Module scope, not built in render: `finePrintComponent` is rendered as an element type inside
// each form, so a wrapper recreated per render would give React a new type every time and remount
// that subtree. Two fixed types mean the identity only changes when the flag itself does.
const SoftBookingFinePrint = props => <SubmitFinePrint {...props} omitYouWontBeChargedMessage />;

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ './BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

const NegotiationForm = loadable(() =>
  import(/* webpackChunkName: "NegotiationForm" */ './NegotiationForm/NegotiationForm')
);

const NegotiationRequestQuoteForm = loadable(() =>
  import(
    /* webpackChunkName: "NegotiationRequestQuoteForm" */ './NegotiationRequestQuoteForm/NegotiationRequestQuoteForm'
  )
);

const AboOrderForm = loadable(() =>
  import(/* webpackChunkName: "AboOrderForm" */ './AboOrderForm/AboOrderForm')
);

// Loaded like the forms: it only ever renders after mount, under a form, so it has no business in
// the main bundle every route downloads.
const PaymentMethodIcons = loadable(() =>
  import(/* webpackChunkName: "PaymentMethodIcons" */ './PaymentMethodIcons/PaymentMethodIcons')
);

// The branch that renders below the price → its form component. See `activeBranch` in OrderPanel.
const BRANCH_FORMS = {
  fixedDuration: BookingFixedDurationForm,
  time: BookingTimeForm,
  dates: BookingDatesForm,
  product: ProductOrderForm,
  abo: AboOrderForm,
  inquiry: InquiryWithoutPaymentForm,
  negotiation: NegotiationForm,
  requestQuote: NegotiationRequestQuoteForm,
};

// Branches whose submit leads into one of our own Stripe checkouts.
const BOOKING_BRANCHES = ['fixedDuration', 'time', 'dates'];

/**
 * The submit label for a signed-out visitor, per branch. Pressing any of these asks for an account
 * first (ListingPage.shared handleSubmit opens the login dialog and resumes afterwards; the
 * negotiation pages sit behind `auth: true`), and the button says so. One table for every form and
 * for the mobile bottom bar, which is the submit itself for inquiry and negotiation listings.
 *
 * ⚠️ The plain labels these replace (BookingDatesForm.requestToBook etc.) are overridden in the
 * Sharetribe Console; these keys are not. Whoever changes a CTA wording in the Console should add
 * the matching `OrderPanel.submitWithLogin.*` entry there too, or the two states of the same button
 * come from different sources.
 */
export const SIGNED_OUT_SUBMIT_LABEL_IDS = {
  fixedDuration: 'OrderPanel.submitWithLogin.booking',
  time: 'OrderPanel.submitWithLogin.booking',
  dates: 'OrderPanel.submitWithLogin.booking',
  product: 'OrderPanel.submitWithLogin.purchase',
  abo: 'OrderPanel.submitWithLogin.abo',
  inquiry: 'OrderPanel.submitWithLogin.inquiry',
  negotiation: 'OrderPanel.submitWithLogin.makeOffer',
  requestQuote: 'OrderPanel.submitWithLogin.requestQuote',
};

// Below this width OrderPanel renders as a fixed-bottom-bar modal (mobile UX).
// At 768px+ (tablet and up) it renders inline within the page's two-column grid.
const MODAL_BREAKPOINT = 767;
const TODAY = new Date();
const ORDER_PANEL_SUBMIT_BUTTON_ID = 'orderPanelSubmitButton';

const isPublishedListing = listing => {
  return listing.attributes.state === LISTING_STATE_PUBLISHED;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const getCheapestPriceVariant = (priceVariants = []) => {
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    return formatMoney(intl, price);
  } catch (e) {
    return `(${price.currency})`;
  }
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const { orderOpen, ...searchParams } = parse(search);
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (isOwnListing, isClosed, isDirectSubmit, onSubmit, history, location) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isDirectSubmit
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;

  if (!showPrice || !price || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
  const priceValue = (
    <span className={css.priceValue}>{formatMoneyIfSupportedCurrency(price, intl)}</span>
  );
  // TEMPORARY (see util/fixdesk.js): fixdesk's `item` unit falls into the empty
  // `other {}` branch of OrderPanel.perUnit — label the monthly price explicitly.
  const perUnitMessage = isFixdeskListing(publicData) ? (
    <FormattedMessage id="Fixdesk.perMonth" />
  ) : (
    <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
  );
  const pricePerUnit = <span className={css.perUnit}>{perUnitMessage}</span>;

  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValueInCTA} title={priceTitle}>
        <FormattedMessage
          id="OrderPanel.priceInMobileCTA"
          values={{ priceValue: formattedPrice }}
        />
      </div>
      <div className={css.perUnitInCTA}>{perUnitMessage}</div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>
        <FormattedMessage id="OrderPanel.price" values={{ priceValue, pricePerUnit }} />
      </p>
    </div>
  );
};

const PriceMissing = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.priceMissing" />
    </p>
  );
};
const InvalidCurrency = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.currencyMismatch" />
    </p>
  );
};

const InvalidPriceVariants = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceVariantsAreInvalid" />
    </p>
  );
};

const hasUniqueVariants = priceVariants => {
  const priceVariantsSlugs = priceVariants?.map(variant =>
    variant.name ? createSlug(variant.name) : 'no-name'
  );
  return new Set(priceVariantsSlugs).size === priceVariants.length;
};

const hasValidPriceVariants = priceVariants => {
  const isArray = Array.isArray(priceVariants);
  const hasItems = isArray && priceVariants.length > 0;
  const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
  const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);

  return variantsHaveNames && namesAreUnique;
};

/**
 * @typedef {Object} ListingTypeConfig
 * @property {string} listingType - The type of the listing
 * @property {string} transactionType - The type of the transaction
 * @property {string} transactionType.process - The process descriptionof the transaction
 * @property {string} transactionType.alias - The alias of the transaction process
 * @property {string} transactionType.unitType - The unit type of the transaction
 */

/**
 * OrderPanel is a component that renders a panel for making bookings, purchases, or inquiries for a listing.
 * It handles different transaction processes and displays appropriate forms based on the listing type.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overwrites the default class for the root element
 * @param {string} [props.className] - Custom class that extends
 * @param {string} [props.titleClassName] - Custom class name for the title
 * @param {propTypes.listing} props.listing - The listing data (either regular or own listing)
 * @param {Array<ListingTypeConfig>} props.validListingTypes - Array of valid listing type configurations
 * @param {boolean} [props.isOwnListing=false] - Whether the listing belongs to the current user
 * @param {boolean} [props.submitRequiresLogin=false] - The visitor is signed out, so pressing the
 *   form's submit first asks for an account: ListingPage.shared handleSubmit opens the login dialog,
 *   keeps the selection and resumes into checkout. The submit label then names that step
 *   ("Anmelden und Buchungsanfrage senden"). Only the ListingPage sets it; every other render site
 *   (TransactionPanel, which is behind `auth: true`) has a signed-in user by construction.
 * @param {boolean} [props.showAcceptedPaymentMethods=false] - Show the accepted-payment-methods row
 *   under the form. Only the ListingPage sets it: its submit is routed into the checkout that
 *   actually takes those methods (soft-booking for providers without Stripe). TransactionPage's
 *   inquiry panel goes to the standard checkout unconditionally, which cannot charge for such a
 *   provider, so it must not show the marks.
 * @param {listingType.user|listingType.currentUser} props.author - The listing author's user data
 * @param {ReactNode} [props.authorLink] - Custom component for rendering the author link
 * @param {ReactNode} [props.payoutDetailsWarning] - Warning message about payout details
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {ReactNode|string} props.title - Title of the panel
 * @param {ReactNode} [props.titleDesktop] - Alternative title for desktop view
 * @param {ReactNode|string} [props.subTitle] - Subtitle text
 * @param {Function} props.onManageDisableScrolling - Handler for managing scroll behavior
 * @param {Function} props.onFetchTimeSlots - Handler for fetching available time slots
 * @param {Object} [props.monthlyTimeSlots] - Available time slots by month
 * @param {Function} props.onFetchTransactionLineItems - Handler for fetching transaction line items
 * @param {Function} [props.onContactUser] - Handler for contacting the listing author
 * @param {Array} [props.lineItems] - Array of line items for the transaction
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {Object} [props.fetchLineItemsError] - Error object if line items fetch failed
 * @param {string} props.marketplaceCurrency - The currency used in the marketplace
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 * @param {'h2'|'h3'} [props.sectionHeadingAs='h3'] - Semantic heading level for form section titles (under the panel title)
 *
 * @returns {JSX.Element} Component that displays the order panel with appropriate form
 */
const OrderPanel = props => {
  const [mounted, setMounted] = useState(false);
  // Read once after mount, never in render: SSR has no host (CLAUDE.md, Topbar pattern).
  const [walletsOnThisHost, setWalletsOnThisHost] = useState(false);
  const intl = useIntl();
  const config = useConfiguration();
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    setMounted(true);
    setWalletsOnThisHost(
      walletsRegisteredForHost(config?.marketplaceRootURL, window.location.host)
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    submitRequiresLogin = false,
    showAcceptedPaymentMethods = false,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    // Ohne Default ein harter Absturz statt eines leeren Kalenders: FieldDateAndTimeInput liest
    // `timeSlotsForDate[id]?.timeSlots` — die Optional-Chain sitzt hinter dem Index, nicht davor.
    // ListingPage liefert die Prop immer (Redux initialisiert sie mit {}), jeder andere Aufrufer
    // muss es nicht wissen müssen.
    timeSlotsForDate = {},
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    showListingImage,
    hideAuthorInfo,
    hidePrice,
    sectionHeadingAs = 'h3',
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  const {
    listingType,
    unitType,
    transactionProcessAlias = '',
    priceVariants,
    startTimeInterval,
    externalBookingUrl: externalBookingUrlRaw,
  } = publicData || {};

  // Lead time ("Vorlaufzeit"): the number of days at the front of the calendar this provider keeps
  // unbookable in order to clarify a request before its start date. Resolved once, here, so every
  // booking form below receives a plain number that is already clamped and sanitised — 0 for every
  // listing that does not configure one, which is what keeps their calendars unchanged.

  // publicData is provider-controlled and this value becomes the primary CTA's href, so it is
  // checked against the partner allowlist here — the one place it is read. A value that fails
  // the check yields null, and every branch below falls back to the normal booking panel.
  const externalBookingUrl = safeExternalBookingUrl(externalBookingUrlRaw);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isInquiry = isInquiryProcess(processName);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const isAbo = isSubscriptionProcess(processName);
  const isPaymentProcess = isBooking || isPurchase || isNegotiation || isAbo;

  // Soft booking = request without payment; the card is charged only when the provider accepts.
  // That promise is the strongest reason to press the button, and it used to surface only in the
  // confirmation e-mail. The panel now says it once, under the form, for every form (the stock
  // "you won't be charged yet" fine print is dropped for these listings so the message is not
  // said twice, and ProductOrderForm — fixdesk — never had it at all).
  //
  // Negotiation is excluded on both sides of that swap. requiresSoftReservationFallback is true
  // for an OFFER listing whose provider has no verified Stripe account, but the replacement hint
  // below deliberately does not render for negotiation — handleOrderSubmit routes those to
  // MakeOfferPage / RequestQuotePage. Without this the stock assurance was dropped and nothing
  // took its place, leaving the quote forms with an empty line where the fine print had been.
  //
  // `hasFullAuthor` is the second guard, and it is about telling the truth. The fallback reads
  // author.profile.publicData.stripeConnected and treats anything but an explicit true as "not
  // verified" — which is right for routing, but wrong for a promise: while the store still holds
  // the sparse author entity from a search query, that check is false for EVERY listing. Without
  // this the panel would tell visitors "no payment until the host confirms" on a verified
  // provider's listing that charges at checkout. Same guard ListingPage.shared.js uses before it
  // decides a route.
  const hasFullAuthor = !!listing?.author?.attributes?.profile?.publicData;
  const isSoftBooking = hasFullAuthor && requiresSoftReservationFallback(listing) && !isNegotiation;
  const FinePrint = isSoftBooking ? SoftBookingFinePrint : SubmitFinePrint;

  const showPriceMissing = isPaymentProcess && !isNegotiation && !price;
  const showInvalidCurrency =
    isPaymentProcess && !isNegotiation && price?.currency !== marketplaceCurrency;

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;

  // An die Zeitzone gekoppelt, weil der Server-Guard es auch ist: ohne Zeitzone im
  // Verfuegbarkeitsplan kann der Boden nur in Serverzeit gerechnet werden, also faellt
  // server/api-util/bookingLeadTime.js dort offen aus. Rechnete der Kalender hier trotzdem einen
  // Boden (getStartOf toleriert eine fehlende Zone und nimmt die des BESUCHERS), spielten beide
  // Seiten verschiedene Regeln — der Kalender sperrte Tage, die der Server anstandslos annimmt.
  // Betrifft die Legacy-Plaene vom Typ availability-plan/day, die laut CLAUDE.md auf Prod neben
  // den zeitbasierten laufen.
  const bookingLeadDays = timeZone ? resolveBookingLeadDays(publicData) : 0;

  // Date placeholder for the hourly and fixed-duration forms. It has to name the first SELECTABLE
  // day, not today: on dev a listing with a 3-day lead time offered "Fr., 11. Sept." above a
  // calendar whose first open day was the 14th. (BookingDatesForm gets no placeholder prop and
  // derives its own.)
  //
  // With no lead time this stays byte-identical to what it was — the raw TODAY, formatted in the
  // VISITOR's zone. Only once a lead time exists does the date become a listing-zone midnight, and
  // then it must be formatted in that zone too or a visitor west of the listing reads the day before.
  const hasBookingLeadTime = bookingLeadDays > 0;
  const datePlaceholder = hasBookingLeadTime
    ? intl.formatDate(getEarliestBookableDate(bookingLeadDays, timeZone, TODAY), {
        ...dateFormattingOptions,
        timeZone,
      })
    : intl.formatDate(TODAY, dateFormattingOptions);
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;

  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = mounted && shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking &&
    [LINE_ITEM_DAY, LINE_ITEM_NIGHT, LINE_ITEM_WEEK, LINE_ITEM_MONTH].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  // Flat monthly membership (Abo): no dates/seats to pick — the form only shows
  // the monthly breakdown, the recurring fine print and the submit CTA.
  // The Abo charges instantly at checkout, so the provider must have a
  // verified Stripe account (mirrored into profile.publicData.stripeConnected).
  const showAboOrderForm = mounted && !isClosed && isAbo;
  const providerStripeConnected = author?.attributes?.profile?.publicData?.stripeConnected === true;

  // Client-only, like everything that shows it: SSR renders signed out (isAuthenticated is false
  // there) while the client hydrates with authInfo already awaited, so a signed-in visitor's first
  // client render must match markup rendered for a signed-out one (React #418/#423). The forms and
  // the bottom bar's direct submit (isDirectSubmit) are mounted-gated too; the guard here keeps the
  // label client-only should either stop being so. OrderPanel.ssr.test.js holds it.
  const requiresLogin = mounted && submitRequiresLogin && !isOwnListing;

  const showInquiryForm = mounted && !isClosed && isInquiry;
  // if listing is a request, we show the negotiation form (reverse negotiation). User (provider) needs to make an offer first.
  const showNegotiationForm = mounted && !isClosed && isNegotiation && unitType === REQUEST;
  // if listing is an offer, we show the "request a quote" form as user needs to ask for a quote first from the provider.
  const showRequestQuoteForm = mounted && !isClosed && isNegotiation && unitType === OFFER;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const searchParams = parse(location.search);
  const isOrderOpen = !!searchParams.orderOpen;
  const preselectedPriceVariantSlug = searchParams.bookableOption;

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  // Note: publicData contains priceVariationsEnabled if listing is created with priceVariations enabled.
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const preselectedPriceVariant =
    Array.isArray(priceVariants) && preselectedPriceVariantSlug && isPriceVariationsInUse
      ? priceVariants.find(pv => pv?.name && createSlug(pv?.name) === preselectedPriceVariantSlug)
      : null;

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
        isPriceVariationsInUse,
        priceVariants,
        priceVariantFieldComponent: PriceVariantPicker,
        preselectedPriceVariant,
        isPublishedListing: isPublishedListing(listing),
      }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
    ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
        priceVariantFieldComponent: PriceVariantPicker,
      }
    : {};

  const showInvalidPriceVariantsMessage =
    isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  const productDelivery = {
    pickupEnabled: pickupEnabled && displayPickup,
    shippingEnabled: shippingEnabled && displayShipping,
    displayDeliveryMethod: displayPickup || displayShipping,
  };

  // The one answer to "what renders below the price". The JSX chain reads it instead of re-deriving
  // the precedence, and so does everything that says something about the submit — the soft-booking
  // promise, the payment row, the signed-out label. Each of them used to rebuild the chain from the
  // show* flags, and they had drifted: the promise showed under a sold-out product, the payment row
  // under ProductOrderForm's no-delivery error.
  const activeBranch = externalBookingUrl
    ? 'external'
    : showPriceMissing
    ? 'priceMissing'
    : showInvalidCurrency
    ? 'invalidCurrency'
    : showInvalidPriceVariantsMessage
    ? 'invalidPriceVariants'
    : showBookingFixedDurationForm
    ? 'fixedDuration'
    : showBookingTimeForm
    ? 'time'
    : showBookingDatesForm
    ? 'dates'
    : showProductOrderForm
    ? 'product'
    : showAboOrderForm
    ? 'abo'
    : showInquiryForm
    ? 'inquiry'
    : showNegotiationForm
    ? 'negotiation'
    : showRequestQuoteForm
    ? 'requestQuote'
    : !isKnownProcess
    ? 'unknownProcess'
    : null;

  // The submit renders but cannot be pressed: sold out, or an Abo whose provider cannot take
  // payments yet. Shared by the mobile bottom bar and everything below.
  const submitBlocked = isOutOfStock || (isAbo && !providerStripeConnected);

  // A submit that renders, can be pressed and leads into one of our own Stripe checkouts.
  // ProductOrderForm shows only an error, no button, without a delivery method (hasDeliveryMethod).
  const leadsToCheckout =
    !isOwnListing &&
    !submitBlocked &&
    (BOOKING_BRANCHES.includes(activeBranch) ||
      (activeBranch === 'product' && hasDeliveryMethod(productDelivery)) ||
      activeBranch === 'abo');

  // The forms are loadable chunks that SSR never renders (they are mounted-gated), so nothing has
  // preloaded them by now. What sits under a form — the soft-booking promise and the payment row —
  // waits for that chunk; rendered earlier it would stand alone under the price, then jump down by
  // the height of the form once the chunk arrives. The payment row's own chunk loads alongside.
  const wantsPaymentRow = showAcceptedPaymentMethods && leadsToCheckout;
  const [readyBranch, setReadyBranch] = useState(null);
  useEffect(() => {
    const ActiveForm = BRANCH_FORMS[activeBranch];
    if (!ActiveForm) {
      return undefined;
    }
    let cancelled = false;
    Promise.all([ActiveForm.load(), wantsPaymentRow ? PaymentMethodIcons.load() : null]).then(
      () => {
        if (!cancelled) {
          setReadyBranch(activeBranch);
        }
      },
      () => {
        // A failed chunk is the form's own problem to surface; nothing under it renders.
      }
    );
    return () => {
      cancelled = true;
    };
  }, [activeBranch, wantsPaymentRow]);
  const formReady = !!BRANCH_FORMS[activeBranch] && readyBranch === activeBranch;

  const showSoftBookingHint = isSoftBooking && leadsToCheckout && formReady;
  const showPaymentRow = wantsPaymentRow && formReady;
  const showWallets = walletsOnThisHost && checkoutOffersWallets(processName);

  const submitLabelId =
    requiresLogin && !submitBlocked ? SIGNED_OUT_SUBMIT_LABEL_IDS[activeBranch] : undefined;
  // Inquiry and negotiation-request listings submit straight from the mobile bottom bar; every
  // other type opens the form there first.
  const isDirectSubmit = showInquiryForm || showNegotiationForm;

  const sharedProps = {
    lineItemUnitType,
    onSubmit,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    isOwnListing,
    marketplaceName,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    sectionHeadingAs,
    submitLabelId,
  };

  const showClosedListingHelpText = listing.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.closedListingSubtitle' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  // Fire the only measurable conversion for the external (affiliate) flow when the
  // user leaves for the partner site — the booking itself completes off-site on LoungePair.
  const handleExternalBookingClick = () =>
    track.loungeBookingClick({
      listing_id: listing?.id?.uuid,
      value: price?.amount ? price.amount / 100 : undefined,
      currency: price?.currency || 'CHF',
      click_url: externalBookingUrl,
      ...adClickIds(),
    });

  const classes = classNames(rootClassName || css.root, className, css.bookingFormScope);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => {
          // history.location, not the render-time `location`: the login dialog is deliberately
          // stacked on top of this panel, and one Escape fires both Modals' body-keyup handlers in
          // the same tick. Whichever runs second would otherwise navigate from a stale snapshot and
          // resurrect the overlay the first one just removed.
          closeOrderModal(history, history.location);
          document.getElementById(ORDER_PANEL_SUBMIT_BUTTON_ID)?.focus();
        }}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          {/* h2: the listing page's only h1 is its title bar */}
          <H1 as="h2" className={css.heading}>
            {title}
          </H1>
        </div>

        {showListingImage && (
          <div className={css.orderHeading}>
            {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
            {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
          </div>
        )}

        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
        />

        {externalBookingUrl ? null : (
          <div className={css.author}>
            <AvatarSmall user={author} className={css.providerAvatar} />
            <span className={css.providerNameLinked}>
              <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
            </span>
            <span className={css.providerNamePlain}>
              <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
            </span>
          </div>
        )}

        {activeBranch === 'external' ? (
          <div className={css.externalBookingPanel}>
            <p className={css.externalIncludedTitle}>
              <FormattedMessage id="OrderPanel.externalIncludedTitle" />
            </p>
            <ul className={css.externalIncluded}>
              <li>
                <Wifi className={css.externalIncludedIcon} size={18} aria-hidden="true" />
                <FormattedMessage id="OrderPanel.externalIncludedWifi" />
              </li>
              <li>
                <Coffee className={css.externalIncludedIcon} size={18} aria-hidden="true" />
                <FormattedMessage id="OrderPanel.externalIncludedRefreshments" />
              </li>
              <li>
                <Armchair className={css.externalIncludedIcon} size={18} aria-hidden="true" />
                <FormattedMessage id="OrderPanel.externalIncludedSeating" />
              </li>
              <li>
                <PlaneTakeoff className={css.externalIncludedIcon} size={18} aria-hidden="true" />
                <FormattedMessage id="OrderPanel.externalIncludedFlightInfo" />
              </li>
            </ul>
            <ul className={css.externalTrust}>
              <li>
                <Zap className={css.externalTrustIcon} size={18} aria-hidden="true" />
                <FormattedMessage id="OrderPanel.externalInstantConfirmation" />
              </li>
              <li>
                <ShieldCheck className={css.externalTrustIcon} size={18} aria-hidden="true" />
                <FormattedMessage id="OrderPanel.externalSecurePayment" />
              </li>
            </ul>
            <ExternalLink
              href={externalBookingUrl}
              className={css.externalBookingCta}
              onClick={handleExternalBookingClick}
            >
              <FormattedMessage id="OrderPanel.ctaButtonExternalBooking" />
            </ExternalLink>
            <div className={css.operator}>
              <span className={css.operatorLabel}>
                <FormattedMessage id="OrderPanel.externalOperatorLabel" />
              </span>
              <img
                className={css.operatorLogo}
                src={loungePairLogo}
                alt="LoungePair"
                width="108"
                height="22"
                loading="lazy"
              />
            </div>
          </div>
        ) : activeBranch === 'priceMissing' ? (
          <PriceMissing />
        ) : activeBranch === 'invalidCurrency' ? (
          <InvalidCurrency />
        ) : activeBranch === 'invalidPriceVariants' ? (
          <InvalidPriceVariants />
        ) : activeBranch === 'fixedDuration' ? (
          <BookingFixedDurationForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingFixedDurationForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            bookingLeadDays={bookingLeadDays}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={datePlaceholder}
            startTimeInterval={startTimeInterval}
            timeZone={timeZone}
            finePrintComponent={FinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : activeBranch === 'time' ? (
          <BookingTimeForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            bookingLeadDays={bookingLeadDays}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={datePlaceholder}
            endDatePlaceholder={datePlaceholder}
            startTimeInterval={startTimeInterval}
            timeZone={timeZone}
            finePrintComponent={FinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : activeBranch === 'dates' ? (
          <BookingDatesForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            bookingLeadDays={bookingLeadDays}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            finePrintComponent={FinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : activeBranch === 'product' ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            {...productDelivery}
            isFixdesk={isFixdeskListing(publicData)}
            onContactUser={onContactUser}
            {...sharedProps}
          />
        ) : activeBranch === 'abo' ? (
          <AboOrderForm
            formId="OrderPanelAboOrderForm"
            providerStripeConnected={providerStripeConnected}
            billingInterval={publicData?.billingInterval}
            {...sharedProps}
          />
        ) : activeBranch === 'inquiry' ? (
          <InquiryWithoutPaymentForm
            formId="OrderPanelInquiryForm"
            onSubmit={onSubmit}
            finePrintComponent={FinePrint}
            isOwnListing={isOwnListing}
            submitLabelId={submitLabelId}
          />
        ) : activeBranch === 'negotiation' ? (
          <NegotiationForm
            formId="OrderPanelNegotiationForm"
            onSubmit={onSubmit}
            finePrintComponent={FinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
            submitLabelId={submitLabelId}
          />
        ) : activeBranch === 'requestQuote' ? (
          <NegotiationRequestQuoteForm
            formId="OrderPanelRequestQuoteForm"
            onSubmit={onSubmit}
            finePrintComponent={FinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
            submitLabelId={submitLabelId}
          />
        ) : activeBranch === 'unknownProcess' ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}

        {/* The no-payment-until-confirmed promise, right under the form's submit button — and only
            where that button renders, can be pressed and leads into a checkout (leadsToCheckout):
            not under an error, a sold-out product or a disabled Abo. It waits for the form's chunk
            (formReady), so it never stands alone under the price. Client-only by construction:
            every input to it is mounted-gated. */}
        {showSoftBookingHint ? (
          <p className={css.softBookingHint}>
            <ShieldCheck className={css.softBookingHintIcon} size={16} aria-hidden="true" />
            <span>
              <FormattedMessage id="OrderPanel.softBookingHint" />
            </span>
          </p>
        ) : null}

        {/* Last line of the panel: how the checkout behind this button is paid, right under the
            promise where there is one. Wallet marks only where the checkout offers them and this
            host can show the wallet button. ListingPage-only (showAcceptedPaymentMethods).
            The spacing sits on a wrapper of our own, not on the row: the row's stylesheet arrives
            with its chunk, after this one, so its list reset (margin: 0) won over a class passed in
            — equal specificity, later rule — and the row sat flush against the modal's edge. */}
        {showPaymentRow ? (
          <div className={css.paymentMethods}>
            <PaymentMethodIcons showWallets={showWallets} />
          </div>
        ) : null}
      </ModalInMobile>
      {externalBookingUrl ? (
        <div className={css.openOrderForm}>
          <PriceMaybe
            price={price}
            publicData={publicData}
            validListingTypes={validListingTypes}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
          />
          <ExternalLink
            href={externalBookingUrl}
            className={css.externalBookingCta}
            onClick={handleExternalBookingClick}
          >
            <FormattedMessage id="OrderPanel.ctaButtonExternalBooking" />
          </ExternalLink>
        </div>
      ) : (
        <div className={css.openOrderForm}>
          <PriceMaybe
            price={price}
            publicData={publicData}
            validListingTypes={validListingTypes}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
            showCurrencyMismatch
          />

          {isClosed ? (
            <div className={css.closedListingButton}>
              <FormattedMessage id="OrderPanel.closedListingButton" />
            </div>
          ) : (
            <PrimaryButton
              id={ORDER_PANEL_SUBMIT_BUTTON_ID}
              onClick={handleSubmit(
                isOwnListing,
                isClosed,
                isDirectSubmit,
                onSubmit,
                history,
                location
              )}
              disabled={submitBlocked}
            >
              {isDirectSubmit && submitLabelId ? (
                <FormattedMessage id={submitLabelId} />
              ) : isAbo ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageAbo" />
              ) : isBooking ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
              ) : isOutOfStock ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
              ) : isPurchase ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
              ) : showNegotiationForm ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageMakeOffer" />
              ) : showRequestQuoteForm ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageRequestAQuote" />
              ) : (
                <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
              )}
            </PrimaryButton>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderPanel;
